import React, { useEffect, useRef, useState } from "react";
import { ShieldCheck, ArrowRight, MessageCircle, Phone, MessageSquareText, Zap, DollarSign, ChevronLeft, ChevronRight, Truck, FileText, Wallet, Play, Pause, Maximize2, Mail, Volume2, VolumeX } from "lucide-react";
import { useSite } from "../../context/SiteContext";
import { links, imgUrl } from "../../lib/api";
import { Container, Reveal, SectionHead, EmailPill } from "../site/Reveal";

const HeroVideo = ({ src, poster }) => {
  const { t } = useSite();
  const ref = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const toggleSound = () => {
    const v = ref.current;
    if (!v) return;
    v.muted = !v.muted;
    if (!v.muted) { v.currentTime = 0; v.play(); setPlaying(true); }
    setMuted(v.muted);
  };
  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };
  const full = () => {
    const v = ref.current;
    if (!v) return;
    (v.requestFullscreen || v.webkitEnterFullscreen || v.webkitRequestFullscreen)?.call(v);
  };
  return (
    <div className="group relative">
      <video
        ref={ref}
        src={src}
        poster={poster}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        className="aspect-square w-full object-cover sm:aspect-[4/3]"
        aria-label="How StripWise works, in a short video"
        data-testid="hero-video"
      />
      {muted && (
        <button onClick={toggleSound} className="absolute left-1/2 top-4 -translate-x-1/2 flex items-center gap-2 rounded-full bg-[#0f2e2a]/80 px-4 py-2 text-[13.5px] font-semibold text-white shadow-lg backdrop-blur hover:bg-[#0f2e2a] transition-colors sw-pulse" data-testid="hero-video-unmute">
          <VolumeX size={15} /> {t("Tap for sound")}
        </button>
      )}
      <div className="absolute bottom-4 right-4 flex gap-2">
        <button onClick={toggleSound} className="flex h-10 w-10 items-center justify-center rounded-full bg-white/85 text-[#0f2e2a] shadow-md backdrop-blur hover:bg-white transition-colors" aria-label={muted ? "Unmute video" : "Mute video"} data-testid="hero-video-sound">
          {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </button>
        <button onClick={toggle} className="flex h-10 w-10 items-center justify-center rounded-full bg-white/85 text-[#0f2e2a] shadow-md backdrop-blur hover:bg-white transition-colors" aria-label={playing ? "Pause video" : "Play video"} data-testid="hero-video-toggle">
          {playing ? <Pause size={16} /> : <Play size={16} />}
        </button>
        <button onClick={full} className="flex h-10 w-10 items-center justify-center rounded-full bg-white/85 text-[#0f2e2a] shadow-md backdrop-blur hover:bg-white transition-colors" aria-label="Full screen video">
          <Maximize2 size={16} />
        </button>
      </div>
    </div>
  );
};

export const Hero = () => {
  const { content, openChat, t } = useSite();
  const h = content?.hero || {};
  const l = links(content);
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  return (
    <section className="relative overflow-hidden bg-[linear-gradient(180deg,#f2fcfa_0%,#ffffff_100%)]" data-testid="hero-section">
      <div className="pointer-events-none absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full bg-[#c9f5ec]/60 blur-3xl" />
      <div className="pointer-events-none absolute -left-40 bottom-[-200px] h-[480px] w-[480px] rounded-full bg-[#f7f3d6]/70 blur-3xl" />
      <Container className="relative grid items-center gap-12 py-14 md:py-16 lg:grid-cols-2 lg:gap-14">
        <div className="sw-fade-up">
          <span className="inline-flex items-center gap-2 rounded-full border border-[#dcefea] bg-white px-3.5 py-1.5 text-[12.5px] font-semibold text-[#0b6f65] shadow-sm">
            <ShieldCheck size={14} /> {h.badge}
          </span>
          <h1 className="mt-6 font-jakarta text-[42px] font-extrabold leading-[1.05] tracking-[-0.035em] text-[#0f2e2a] sm:text-[54px] lg:text-[60px]" data-testid="hero-title">
            {h.title} <span className="text-[#0e8c7f]">{h.highlight}</span>
          </h1>
          <p className="mt-5 max-w-[520px] text-[18px] leading-[1.6] text-[#4b5d5a]">{h.subtitle}</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <button onClick={() => scrollTo("contact")} className="sw-btn-primary text-[16.5px]" data-testid="hero-quote-btn">
              {t("Get a Free Quote")} <ArrowRight size={17} />
            </button>
            <button onClick={() => openChat(t("Hi! I'd like to start selling my supplies."))} className="sw-btn-outline text-[16.5px]" data-testid="hero-chat-btn">
              <MessageCircle size={17} /> {t("Chat & Start Selling")}
            </button>
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-2.5">
            <span className="text-[14px] font-medium text-[#5b6d6a]">{t("Rather talk now?")}</span>
            <a href={l.tel} className="sw-chip" data-testid="hero-call"><Phone size={14} />{t("Call")} {content?.phone_display}</a>
            <a href={l.sms} className="sw-chip" data-testid="hero-text"><MessageSquareText size={14} />{t("Text Us")}</a>
            <button onClick={() => openChat()} className="sw-chip" data-testid="hero-live-chat"><MessageCircle size={14} />{t("Live Chat")}</button>
          </div>
          <EmailPill className="mt-3" />
          <div className="mt-8 flex flex-wrap gap-7">
            {[[Zap, "Free quote", "No obligation"], [DollarSign, "Free shipping", "Prepaid label"], [ShieldCheck, "Real people", "Here to help"]].map(([I, a, b]) => (
              <div key={a} className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#d9f7f0] text-[#0e8c7f]"><I size={17} /></span>
                <div className="leading-tight">
                  <div className="text-[14.5px] font-semibold text-[#0f2e2a]">{t(a)}</div>
                  <div className="text-[12.5px] text-[#6b7c79]">{t(b)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="sw-fade-up" style={{ animationDelay: "150ms" }}>
          <div className="relative">
          <div className="relative overflow-hidden rounded-[28px] bg-[#e6f4f1] shadow-[0_40px_80px_-30px_rgba(15,46,42,0.35)]">
            {h.video ? (
              <HeroVideo key={h.video} src={imgUrl(h.video)} poster={imgUrl(h.poster || h.image)} />
            ) : (
              <img src={imgUrl(h.image)} alt="Sell your sealed, unused testing supplies with StripWise" className="aspect-square sm:aspect-[4/3] w-full object-cover" />
            )}
          </div>
          <div className="absolute -bottom-6 left-0 sm:-left-5 flex items-center gap-3 rounded-2xl bg-white px-5 py-3.5 shadow-[0_20px_40px_-14px_rgba(15,46,42,.3)] sw-float">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#0e8c7f] text-white font-bold">$</span>
            <div>
              <div className="font-jakarta text-[16px] font-bold text-[#0f2e2a]">{h.payout_title}</div>
              <div className="text-[12.5px] text-[#6b7c79]">{h.payout_text}</div>
            </div>
          </div>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-2.5 sm:grid-cols-3" data-testid="hero-video-contact-bar">
            <button onClick={() => openChat()} className="group flex items-center gap-3 rounded-2xl bg-[#0e8c7f] px-4 py-3 text-left text-white shadow-[0_12px_28px_-12px_rgba(14,140,127,.7)] hover:bg-[#0b7569] transition-colors" data-testid="video-bar-live-chat">
              <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/15">
                <MessageCircle size={18} />
                <span className="absolute right-0 top-0 h-2.5 w-2.5 rounded-full border-2 border-[#0e8c7f] bg-[#4ade80]" />
              </span>
              <span className="leading-tight"><span className="block text-[15px] font-bold">{t("Chat With Us")}</span><span className="text-[12.5px] text-white/80">{t("Live now · fast replies")}</span></span>
            </button>
            <a href={l.tel} className="flex items-center gap-3 rounded-2xl border border-[#d6efe9] bg-white px-4 py-3 hover:border-[#0e8c7f] hover:bg-[#f4fbf9] transition-colors" data-testid="video-bar-call">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#0f2e2a] text-white"><Phone size={17} /></span>
              <span className="leading-tight"><span className="block text-[15px] font-bold text-[#0f2e2a]">{t("Call / Text")}</span><span className="text-[12.5px] text-[#5b6d6a]">{content?.phone_display}</span></span>
            </a>
            <a href={l.wa} target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-2xl border border-[#d6efe9] bg-white px-4 py-3 hover:border-[#25a35a] hover:bg-[#f3fbf6] transition-colors" data-testid="video-bar-whatsapp">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#25a35a] text-white"><MessageCircle size={17} /></span>
              <span className="leading-tight"><span className="block text-[15px] font-bold text-[#0f2e2a]">WhatsApp</span><span className="text-[12.5px] text-[#5b6d6a]">{content?.whatsapp_display}</span></span>
            </a>
            <a href={l.mail} className="flex items-center gap-3 rounded-2xl border border-[#bfe7de] bg-[#f4fbf9] px-4 py-3 hover:border-[#0e8c7f] transition-colors sm:col-span-3" data-testid="video-bar-email">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#0e8c7f] text-white"><Mail size={17} /></span>
              <span className="min-w-0 leading-tight"><span className="block text-[15px] font-bold text-[#0f2e2a]">{t("Email Us")}</span><span className="block truncate text-[14px] font-semibold text-[#0b6f65]">{content?.email}</span></span>
            </a>
            {content?.email2 && <a href={l.mail2} className="flex items-center gap-3 rounded-2xl border border-[#bfe7de] bg-[#f4fbf9] px-4 py-3 hover:border-[#0e8c7f] transition-colors sm:col-span-3" data-testid="video-bar-email2">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#0e8c7f] text-white"><Mail size={17} /></span>
              <span className="min-w-0 leading-tight"><span className="block text-[15px] font-bold text-[#0f2e2a]">{t("Email Us")}</span><span className="block truncate text-[14px] font-semibold text-[#0b6f65]">{content.email2}</span></span>
            </a>}
          </div>
        </div>
      </Container>
    </section>
  );
};

const SLIDE_BG = { teal: "bg-[#0e8c7f]", orange: "bg-[#f7931e]", dark: "bg-[#12302c]" };

export const PromoSlider = () => {
  const { content } = useSite();
  const slides = content?.slides || [];
  const [i, setI] = useState(0);
  useEffect(() => {
    if (!slides.length) return;
    const t = setInterval(() => setI((x) => (x + 1) % slides.length), 5500);
    return () => clearInterval(t);
  }, [slides.length]);
  if (!slides.length) return null;
  const go = (d) => setI((x) => (x + d + slides.length) % slides.length);
  return (
    <section className="pb-6 pt-16 md:pt-20" data-testid="promo-slider">
      <Container>
        <Reveal className="relative overflow-hidden rounded-[28px] shadow-[0_30px_60px_-30px_rgba(15,46,42,.4)]">
          <div className="flex transition-transform duration-700 ease-[cubic-bezier(.2,.7,.2,1)]" style={{ transform: `translateX(-${i * 100}%)` }}>
            {slides.map((s, idx) => (
              <div key={idx} className={`grid w-full shrink-0 md:grid-cols-2 ${SLIDE_BG[s.color] || SLIDE_BG.teal}`}>
                <div className="flex flex-col justify-center px-8 py-10 md:px-14 md:py-14 text-white">
                  <span className="text-[12.5px] font-bold uppercase tracking-[0.12em] text-white/80">{s.eyebrow}</span>
                  <h3 className="mt-3 font-jakarta text-[30px] md:text-[40px] font-extrabold leading-[1.1] tracking-[-0.02em]">{s.title}</h3>
                  <p className="mt-4 max-w-md text-[17px] leading-relaxed text-white/85">{s.text}</p>
                </div>
                <div className="h-[220px] md:h-[340px]">
                  <img src={imgUrl(s.image)} alt={s.title} className="h-full w-full object-cover" />
                </div>
              </div>
            ))}
          </div>
          <button onClick={() => go(-1)} className="absolute left-3 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-white/30 text-white backdrop-blur hover:bg-white/50 transition-colors" aria-label="Previous slide" data-testid="slider-prev"><ChevronLeft size={20} /></button>
          <button onClick={() => go(1)} className="absolute right-3 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-white/30 text-white backdrop-blur hover:bg-white/50 transition-colors" aria-label="Next slide" data-testid="slider-next"><ChevronRight size={20} /></button>
          <div className="absolute bottom-5 left-8 md:left-auto md:right-[52%] flex gap-1.5">
            {slides.map((_, idx) => (
              <button key={idx} onClick={() => setI(idx)} className={`h-2 rounded-full transition-[width,background-color] duration-300 ${idx === i ? "w-7 bg-white" : "w-2 bg-white/50"}`} aria-label={`Slide ${idx + 1}`} />
            ))}
          </div>
        </Reveal>
      </Container>
    </section>
  );
};

const STEPS = [
  [FileText, "Get a Quote", "Tell us which sealed, unexpired items you have using our short form or live chat. We'll reply with a clear, no-obligation offer."],
  [Truck, "Ship for Free", "Like the offer? We send a prepaid shipping label with your quote. Pack your box, drop it off, and shipping costs you nothing."],
  [Wallet, "Get Paid Your Way", "Once your package arrives and is checked, we pay you with the method you picked: Zelle, PayPal, Venmo, Cash App, Chime or ACH."],
];

export const HowItWorks = () => {
  const { t } = useSite();
  return (
  <section id="how-it-works" className="scroll-mt-28 py-20 md:py-24" data-testid="how-it-works">
    <Container>
      <SectionHead eyebrow={t("How It Works")} title={t("Three easy steps, from extra boxes to a finished sale")} />
      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {STEPS.map(([I, title, d], idx) => (
          <Reveal key={title} delay={idx * 120} className="sw-card relative bg-[linear-gradient(180deg,#f7fdfc,#ffffff)] p-8">
            <span className="absolute right-7 top-5 font-jakarta text-[48px] font-extrabold text-[#d5f3ed]">0{idx + 1}</span>
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[linear-gradient(135deg,#12a593,#0b6f65)] text-white shadow-md"><I size={20} /></span>
            <h3 className="mt-8 text-[20px] font-bold text-[#0f2e2a]">{t(title)}</h3>
            <p className="mt-3 text-[15.5px] leading-relaxed text-[#566865]">{t(d)}</p>
          </Reveal>
        ))}
      </div>
    </Container>
  </section>
  );
};
